// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef } from 'react'
import type { ReactElement } from 'react'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, User } from 'lucide-react'

import styles from './CameraOrbitPicker.module.css'

// ── Angle helpers ──

/** Map azimuth (0-360) + elevation to a human-readable label. */
function getAngleLabel(azimuth: number, elevation: number): string {
    // Normalize azimuth to 0-360
    const az = ((azimuth % 360) + 360) % 360

    let horizontal: string
    if (az <= 22.5 || az > 337.5) horizontal = 'Front'
    else if (az <= 67.5) horizontal = 'Front-Right'
    else if (az <= 112.5) horizontal = 'Right'
    else if (az <= 157.5) horizontal = 'Back-Right'
    else if (az <= 202.5) horizontal = 'Back'
    else if (az <= 247.5) horizontal = 'Back-Left'
    else if (az <= 292.5) horizontal = 'Left'
    else horizontal = 'Front-Left'

    let vertical = ''
    if (elevation > 15) vertical = ', Above'
    else if (elevation < -15) vertical = ', Below'

    return `${horizontal}${vertical}`
}

// ── Wireframe rings ──

const VERTICAL_RING_ANGLES = [0, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165]
const HORIZONTAL_RING_OFFSETS = [
    { size: 193, y: 26 },
    { size: 193, y: -26 },
    { size: 173, y: 50 },
    { size: 173, y: -50 },
    { size: 141, y: 71 },
    { size: 141, y: -71 },
    { size: 100, y: 87 },
    { size: 100, y: -87 },
]

// ── Component ──

interface CameraOrbitPickerProps {
    thumbnailUrl?: string
    azimuth: number
    elevation: number
    zoom: number
    onAngleChange: (azimuth: number, elevation: number) => void
    onZoomChange: (zoom: number) => void
}

const STEP_DEG = 15

// Rotation slider: 0–315, step 45
const ROTATION_MIN = 0
const ROTATION_MAX = 315
const ROTATION_STEP = 45

// Tilt slider: -30–90, step 30
const TILT_MIN = -30
const TILT_MAX = 90
const TILT_STEP = 30

// Zoom slider: 0–10, step 5
const ZOOM_MIN = 0
const ZOOM_MAX = 10
const ZOOM_STEP = 5

function sliderPercent(value: number, min: number, max: number): number {
    return ((value - min) / (max - min)) * 100
}

export default function CameraOrbitPicker({
    thumbnailUrl,
    azimuth,
    elevation,
    zoom,
    onAngleChange,
    onZoomChange,
}: CameraOrbitPickerProps): ReactElement {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const dragging = useRef(false)
    const lastPos = useRef({ x: 0, y: 0 })
    // Use refs for smooth drag without re-renders per pixel
    const liveAz = useRef(azimuth)
    const liveEl = useRef(elevation)

    // Sync props → refs
    useEffect(() => {
        liveAz.current = azimuth
        liveEl.current = elevation
    }, [azimuth, elevation])

    const clampElevation = (el: number) => Math.max(-30, Math.min(90, el))

    // ── Drag handlers ──

    const onPointerDown = useCallback((e: React.PointerEvent) => {
        dragging.current = true
        lastPos.current = { x: e.clientX, y: e.clientY }
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    }, [])

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!dragging.current) return
        const dx = e.clientX - lastPos.current.x
        const dy = e.clientY - lastPos.current.y
        lastPos.current = { x: e.clientX, y: e.clientY }

        const sensitivity = 0.8
        const newAz = liveAz.current + dx * sensitivity
        const newEl = clampElevation(liveEl.current - dy * sensitivity)

        liveAz.current = newAz
        liveEl.current = newEl
        onAngleChange(newAz, newEl)
    }, [onAngleChange])

    const onPointerUp = useCallback(() => {
        dragging.current = false
    }, [])

    // ── Arrow buttons ──

    const nudge = useCallback((dAz: number, dEl: number) => {
        const newAz = azimuth + dAz
        const newEl = clampElevation(elevation + dEl)
        onAngleChange(newAz, newEl)
    }, [azimuth, elevation, onAngleChange])

    const label = getAngleLabel(azimuth, elevation)
    const globeTransform = `rotateX(${-elevation}deg) rotateY(${azimuth}deg)`
    const cameraOrbitTransform = `rotateX(${-elevation}deg) rotateY(${azimuth}deg)`

    // Snap continuous values to nearest slider step for display
    const snappedAz = Math.round(((azimuth % 360 + 360) % 360) / ROTATION_STEP) * ROTATION_STEP
    const snappedEl = Math.round(elevation / TILT_STEP) * TILT_STEP

    return (
        <>
        <div
            ref={wrapperRef}
            className={styles.wrapper}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
        >
            <div className={styles.instruction}>
                Hold and drag to change<br />camera angle
            </div>

            {/* Unified 3D scene: globe + center image + camera share depth sorting */}
            <div className={styles.scene}>
                {/* Wireframe globe */}
                <div className={styles.globe} style={{ transform: globeTransform }}>
                    {VERTICAL_RING_ANGLES.map((angle) => (
                        <div
                            key={`v-${angle}`}
                            className={styles.ring}
                            style={{ transform: `rotateY(${angle}deg)` }}
                        />
                    ))}
                    <div className={styles.ring} style={{ transform: 'rotateX(90deg)' }} />
                    {HORIZONTAL_RING_OFFSETS.map(({ size, y }, i) => (
                        <div
                            key={`h-${i}`}
                            className={styles.ringHorizontal}
                            style={{
                                width: `${size}px`,
                                height: `${size}px`,
                                transform: `translate(-50%, -50%) translateY(${y}px) rotateX(90deg)`,
                            }}
                        />
                    ))}
                </div>

                {/* Center thumbnail at Z=0 — participates in 3D depth sorting */}
                <div className={styles.centerSubject}>
                    {thumbnailUrl ? (
                        <img src={thumbnailUrl} alt="" className={styles.centerThumb} />
                    ) : (
                        <div className={styles.centerPlaceholder}>
                            <User className={styles.centerPlaceholderIcon} />
                        </div>
                    )}
                </div>

                {/* Camera model orbiting at Z=100px */}
                <div className={styles.cameraOrbit} style={{ transform: cameraOrbitTransform }}>
                    <div className={styles.cameraBody} style={{ transform: 'translateZ(100px)' }}>
                        <div className={styles.camFront}>
                            <div className={styles.camLens}>
                                <div className={styles.camLensInner} />
                            </div>
                            <div className={styles.camRec} />
                        </div>
                        <div className={styles.camBack}>
                            <div className={styles.camScreen} />
                        </div>
                        <div className={styles.camTop}>
                            <div className={styles.camDial} />
                        </div>
                        <div className={styles.camBottom} />
                        <div className={styles.camLeft} />
                        <div className={styles.camRight} />
                        <div className={styles.camViewfinder}>
                            <div className={styles.camViewfinderBody}>
                                <div className={styles.camViewfinderLens} />
                            </div>
                        </div>
                        <div className={styles.camLine} />
                    </div>
                </div>
            </div>

            {/* Arrow buttons */}
            <button
                type="button"
                className={`${styles.arrowButton} ${styles.arrowUp}`}
                onClick={(e) => { e.stopPropagation(); nudge(0, STEP_DEG) }}
                aria-label="Tilt up"
            >
                <ChevronUp size={24} />
            </button>
            <button
                type="button"
                className={`${styles.arrowButton} ${styles.arrowDown}`}
                onClick={(e) => { e.stopPropagation(); nudge(0, -STEP_DEG) }}
                aria-label="Tilt down"
            >
                <ChevronDown size={24} />
            </button>
            <button
                type="button"
                className={`${styles.arrowButton} ${styles.arrowLeft}`}
                onClick={(e) => { e.stopPropagation(); nudge(-STEP_DEG, 0) }}
                aria-label="Rotate left"
            >
                <ChevronLeft size={24} />
            </button>
            <button
                type="button"
                className={`${styles.arrowButton} ${styles.arrowRight}`}
                onClick={(e) => { e.stopPropagation(); nudge(STEP_DEG, 0) }}
                aria-label="Rotate right"
            >
                <ChevronRight size={24} />
            </button>

            {/* Angle label */}
            <div className={styles.angleLabel}>{label}</div>
        </div>

        {/* Sliders */}
        <div className={styles.sliders}>
            {/* Rotation */}
            <div className={styles.sliderRow}>
                <div
                    className={styles.sliderFill}
                    style={{ width: `${sliderPercent(snappedAz, ROTATION_MIN, ROTATION_MAX)}%` }}
                />
                <div
                    className={styles.sliderIndicator}
                    style={{ left: `${sliderPercent(snappedAz, ROTATION_MIN, ROTATION_MAX)}%` }}
                />
                <div className={styles.sliderLabels}>
                    <span className={styles.sliderName}>Rotation</span>
                    <span className={styles.sliderValue}>{snappedAz}°</span>
                </div>
                <input
                    type="range"
                    className={styles.sliderInput}
                    min={ROTATION_MIN}
                    max={ROTATION_MAX}
                    step={ROTATION_STEP}
                    value={snappedAz}
                    onChange={(e) => onAngleChange(Number(e.target.value), elevation)}
                />
            </div>

            {/* Tilt */}
            <div className={styles.sliderRow}>
                <div
                    className={styles.sliderFill}
                    style={{ width: `${sliderPercent(snappedEl, TILT_MIN, TILT_MAX)}%` }}
                />
                <div
                    className={styles.sliderIndicator}
                    style={{ left: `${sliderPercent(snappedEl, TILT_MIN, TILT_MAX)}%` }}
                />
                <div className={styles.sliderLabels}>
                    <span className={styles.sliderName}>Tilt</span>
                    <span className={styles.sliderValue}>{snappedEl}°</span>
                </div>
                <input
                    type="range"
                    className={styles.sliderInput}
                    min={TILT_MIN}
                    max={TILT_MAX}
                    step={TILT_STEP}
                    value={snappedEl}
                    onChange={(e) => onAngleChange(azimuth, Number(e.target.value))}
                />
            </div>

            {/* Zoom */}
            <div className={styles.sliderRow}>
                <div
                    className={styles.sliderFill}
                    style={{ width: `${sliderPercent(zoom, ZOOM_MIN, ZOOM_MAX)}%` }}
                />
                <div
                    className={styles.sliderIndicator}
                    style={{ left: `${sliderPercent(zoom, ZOOM_MIN, ZOOM_MAX)}%` }}
                />
                <div className={styles.sliderLabels}>
                    <span className={styles.sliderName}>Zoom</span>
                    <span className={styles.sliderValue}>{zoom}</span>
                </div>
                <input
                    type="range"
                    className={styles.sliderInput}
                    min={ZOOM_MIN}
                    max={ZOOM_MAX}
                    step={ZOOM_STEP}
                    value={zoom}
                    onChange={(e) => onZoomChange(Number(e.target.value))}
                />
            </div>
        </div>
        </>
    )
}

export { getAngleLabel }
